import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { getConfiguredApiBase } from '../api/client'
import ApiKeysSection from '../components/settings/ApiKeysSection'
import AdminToolNav from '../components/admin/AdminToolNav'

/**
 * Admin-only surface for minting `dwk_` API keys (gated by ApiKeysAdminRoute).
 *
 * Mounts the same `ApiKeysSection` the account-settings form uses, but with
 * `persistentReveal` so a key stays readable across reloads from this browser's
 * local cache (see `utils/adminApiKeyStore` for the security posture — the
 * server still never returns a raw key twice, RFC-0822).
 */
export default function AdminApiKeysPage() {
  const { t } = useTranslation('pages')
  const { session } = useAuth()
  const apiBase = getConfiguredApiBase()

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6" data-testid="admin-api-keys-page">
      <AdminToolNav />
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-foreground mb-1">{t('adminApiKeys.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('adminApiKeys.description')}</p>
      </div>

      <div className="mb-4 p-3 rounded-lg border border-warning/40 bg-warning/10">
        <p className="text-xs font-medium text-warning" data-testid="admin-api-keys-storage-notice">
          {t('adminApiKeys.storedLocallyNotice')}
        </p>
      </div>

      {session ? (
        <div className="rounded-xl border border-border bg-surface-1 p-4">
          <ApiKeysSection persistentReveal />
        </div>
      ) : (
        // Reachable in local dev, where ApiKeysAdminRoute intentionally bypasses
        // the admin claim. Without a session `listApiKeys()` would resolve to the
        // non-UUID `debug-user` and 500, so say so instead of rendering an
        // empty panel that looks broken.
        <div
          className="rounded-xl border border-border bg-surface-1 p-4"
          data-testid="admin-api-keys-signed-out"
        >
          <p className="text-sm text-foreground/70 mb-2">{t('adminApiKeys.signInRequired')}</p>
          <Link to="/login" className="text-sm text-accent hover:underline">
            {t('adminApiKeys.signInLink')}
          </Link>
        </div>
      )}

      <section className="mt-8" data-testid="admin-api-keys-usage">
        <h2 className="text-base font-semibold text-foreground mb-1">
          {t('adminApiKeys.usageHeading')}
        </h2>
        <p className="text-xs font-medium text-foreground/50 mb-3">{t('adminApiKeys.usageIntro')}</p>

        <ul className="mb-3 pl-5 list-disc space-y-1 text-xs font-medium text-foreground/60">
          <li>{t('adminApiKeys.usageAffinity')}</li>
          <li>{t('adminApiKeys.usageChatFirst')}</li>
        </ul>

        <pre className="bg-surface-2 border border-border rounded-lg p-4 overflow-x-auto text-xs font-medium leading-relaxed text-foreground/80">
          {`TOKEN="dwk_..."            # the key created above
BASE="${apiBase}"
PROJECT_ID="my-project"
JAR="$(mktemp)"            # session-affinity cookie jar — required in production

# 1. Chat FIRST. The chat path creates the project workspace on this instance; a
#    REST client whose first call is /agentic/director/tools/* gets a 404 instead.
curl -sS -X POST "$BASE/projects/$PROJECT_ID/agentic/agent/chat" \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -b "$JAR" -c "$JAR" \\
  -b "DREAMWRIGHT_PROJECT=$PROJECT_ID" \\
  -d '{"prompt": "Create a hero character named Aria", "timeout": 300, "auto_confirm_cost": true}'

# 2. Inspect the canvas. Same cookie jar, same instance.
curl -sS -X POST "$BASE/projects/$PROJECT_ID/agentic/director/tools/get_project_status" \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -b "$JAR" -c "$JAR" -b "DREAMWRIGHT_PROJECT=$PROJECT_ID" -d '{}'

curl -sS -X POST "$BASE/projects/$PROJECT_ID/agentic/director/tools/inspect_node" \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -b "$JAR" -c "$JAR" -b "DREAMWRIGHT_PROJECT=$PROJECT_ID" \\
  -d '{"node_id": "<node-id-from-get_project_status>"}'`}
        </pre>

        <p className="text-xs font-medium text-foreground/50 mt-3">
          {t('adminApiKeys.usageFullGuidePrefix')}{' '}
          <Link to="/for-agents" className="text-accent hover:underline">
            {t('adminApiKeys.usageFullGuideLink')}
          </Link>
          .
        </p>
      </section>
    </div>
  )
}
